'use client';

import BorderedCircle from '../icon/BorderedCircle';
import BorderedRectangle from '../icon/BorderedRectangle';
import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import TitleH1 from '../common/TitleH1';
import CategoryLabel from '../category/CategoryLabel';
import DateLabel from '../date/DateLabel';
import DirectusImage from '../image/DirectusImage';

type Url = {
  url: string;
  external: boolean;
  slug?: string;
};

type HeroInfo = {
  title: string;
  description?: string;
  callToActionUrl: Url;
  category?: string;
  image?: string;
  imageBlur?: string;
  isoDate?: string;
};

interface Props {
  id: string;
  data: Array<HeroInfo>;
  styles?: string;
}

export default function Hero({ data, id, styles }: Props) {
  const heroRef = useRef<HTMLDivElement | null>(null);
  const [offsetY, setOffsetY] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleScroll = () => setOffsetY(window?.scrollY);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const nextIndex = (currentIndex + 1) % data.length;
      onScrollToImage({ preventDefault: () => {} } as any, nextIndex);
    }, 5000);

    return () => {
      clearInterval(interval);
    };
  }, [currentIndex, data.length]);

  const onScrollToImage = (e: React.MouseEvent<HTMLAnchorElement>, index: number) => {
    e.preventDefault();
    setCurrentIndex(index);

    const container = heroRef.current;
    if (container) {
      const nextTargetElement = document.getElementById(`${id}-item${index + 1}`);
      if (nextTargetElement) {
        const containerRect = container.getBoundingClientRect();
        const targetRect = nextTargetElement.getBoundingClientRect();
        const scrollOffset = targetRect.left - containerRect.left;
        container.scrollTo({
          left: container.scrollLeft + scrollOffset,
          behavior: 'smooth',
        });
      }
    }
  };

  return (
    <div className="rounded-none bg-hero-mobile-ellipse parallax-container md:rounded-md md:bg-hero-ellipse">
      <div className="absolute bottom-0 z-[1] h-auto w-full rounded-none md:bottom-auto md:right-[0px] md:h-hero md:w-10 md:rounded-br-md">
        <div className="md:w-50px absolute inset-0 w-full bg-bar-hero-horizontal-gradient md:h-full md:bg-bar-hero-vertical-gradient" />
      </div>
      <div
        className={`carousel carousel-center w-full ${styles} h-hero-mobile lg:h-hero`}
        ref={heroRef}
      >
        {data.map(
          (
            {
              category,
              image,
              imageBlur,
              isoDate,
              title,
              callToActionUrl: { url, external, slug },
            },
            indexSlide,
          ) => {
            return (
              <div
                key={indexSlide}
                id={`${id}-item${indexSlide + 1}`}
                className="carousel-item w-full animate-fadeIn overflow-hidden rounded-none group md:rounded-md"
              >
                <div className="relative h-full w-full justify-items-start overflow-hidden">
                  <div className="absolute inset-0 bg-white" />
                  <Link href={slug ? `${url}/${slug}` : url} target={external ? '_blank' : '_self'} prefetch={false}>
                    <DirectusImage
                      src={image || ''}
                      alt={title || ''}
                      dimension={{ sizes: '(max-width: 1400px) 100vw, 100vw' }}
                      blur={imageBlur}
                      styles={`rounded-none md:rounded-md`}
                      extraStyles={{ transform: `translateY(${offsetY * 0.5}px)` }}
                      priority={indexSlide === 0}
                    />

                    <div className="hero-content relative flex h-full max-w-full items-end justify-between p-0">
                      <div
                        className="absolute h-full w-full bg-black opacity-20"
                        style={{ transform: `translateY(${offsetY * 0.5}px)` }}
                      ></div>
                      {indexSlide === 0 && (
                        <div className="absolute bottom-[0px] flex h-[50%] w-full justify-start bg-gradient-to-t from-astro-gradient-start to-astro-gradient-end p-[15px] pb-[130px] text-left text-neutral-content transition-all duration-1000 ease-out md:mb-auto md:px-[44px] md:py-[60px]">
                          <div className="flex h-full max-w-md flex-col items-start justify-end gap-8">
                            <div className="flex items-center gap-5">
                              {category && <CategoryLabel category={category} type={'blur'} />}
                              {isoDate && (
                                <DateLabel
                                  isoDate={isoDate}
                                  type={'since'}
                                  styles="text-xs text-white opacity-60 font-semibold"
                                />
                              )}
                            </div>
                            <TitleH1 size="big" heading={false}>
                              {title}
                            </TitleH1>
                          </div>
                        </div>
                      )}
                      {indexSlide !== 0 && (
                        <div className="absolute bottom-[0px] flex h-[50%] w-full items-end justify-start bg-gradient-to-t from-astro-gradient-start to-astro-gradient-end p-[15px] pb-[130px] text-left text-neutral-content transition-all duration-1000 ease-out md:mb-auto md:px-[44px] md:py-[60px]">
                          <div className="flex h-full max-w-md flex-col items-start justify-end gap-8">
                            <div className="flex items-center gap-5">
                              {category && <CategoryLabel category={category} type={'blur'} />}
                              {isoDate && (
                                <DateLabel
                                  isoDate={isoDate}
                                  type={'since'}
                                  styles="text-xs text-white opacity-60 font-semibold"
                                />
                              )}
                            </div>
                            <TitleH1 size="big" heading={false}>
                              {title}
                            </TitleH1>
                          </div>
                        </div>
                      )}
                    </div>
                  </Link>
                  <div className="absolute bottom-[60px] z-10 mb-0 flex h-10 w-full items-center justify-center md:bottom-[0px] md:right-[80px] md:mb-[60px] md:w-auto">
                    <div className="flex w-full items-center justify-center justify-items-center gap-2 py-2 md:justify-end">
                      {data.map((_, index) => {
                        return (
                          <Link
                            key={index}
                            href={`#${id}-item${index + 1}`}
                            target="_self"
                            title="Navigation"
                            onClick={(e) => onScrollToImage(e, index)}
                          >
                            {indexSlide === index ? <BorderedRectangle /> : <BorderedCircle />}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            );
          },
        )}
      </div>
    </div>
  );
}
